import { Injectable } from '@angular/core';
import { ContentService, DcrKeyPipe } from '@blk/cwp-ng-framework';
import { BusinessComponentOptions, ReportSection } from '@blk/simba';
import { ASSET_CLASS_LABEL_MAPPING } from '@ddt-web/common';
import { ViewCategory } from '@ddt-web/config';
import { InjectConfig, LocaleUtilResolverService, ModelsExperienceConfigKey, PdfSharedConfigKey } from '@ddt-web/config-layer';
import { MappedValueFormatter, TabUtil } from '@ddt-web/data-service';
import { ClientOutput, OutputFlag, SharedApplicationConstants } from '@ddt-web/domain';
import { InjectQueryParam, ModelNameUtil, StringUtil } from '@ddt-web/generic-utils';
import { MarketViewConfigService } from '@ddt-web/market-view';
import {
	ESGMetricsHeadings,
	ESGScoreImpact,
	FeesComponent,
	FreeFormComponent,
	GlossaryComponent,
	HeadingLevel,
	LoadAdjustedReturnsComponent,
	MarketViewsComponent,
	PageBreakBusinessComponent,
	SectionTitleComponent,
	StandardizedPerformanceMarketComponent,
	StandardizedPerformanceNavComponent
} from '@ddt-web/pdf-components';
import { Countries, ReportConfigService } from '@ddt-web/pdf-generate';
import { AchoReportConfiguration, ReportType } from '@ddt-web/pdf-utils';
import { Replacer } from '@ddt-web/rules-engine';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { MeQueryParamKey, UrlService } from '@ddt-web/url';
import * as dayjs from 'dayjs';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest, Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { ModelsBrowseStoreConfig } from '../config/store.config';
import { ModelPortfolio } from '../models';
import {
	CommentaryComponent,
	EsgLowerCarbonFootprintComponent,
	EsgMsciRatingsComponent,
	EsgUpliftComponent,
	FamilyChangesToHoldingsComponent,
	FamilyLatestHoldingsBodyComponent,
	FamilyLatestHoldingsHeaderComponent,
	FamilyPerformanceComponent
} from '../pdf-components';
import { InvestmentGuidingPrinciples, InvestmentProcessComponent } from '../pdf-components/investment';
import { ModelOverview } from '../pdf-components/model-overview';
import { PerformanceCommentaryComponent } from '../pdf-components/performance-commentary';
import { ADVISOR_LANDSCAPE_LAYOUT, AdvisorReportCoverPage } from '../pdf-components/static';
import { FamiliesUtil } from '../utils';
import { ModelsBrowseDataService } from './models-browse-data.service';

@Injectable({
	providedIn: 'root'
})
export class ModelsBrowseReportConfigService extends ReportConfigService {
	@InjectConfig(ModelsExperienceConfigKey.MODEL_CATEGORY_MAPPING) private readonly modelCategoryMapping: { [key: string]: string } = {};
	@InjectConfig(ModelsExperienceConfigKey.PDF_INVESTMENT_PAGES_MAPPING) private readonly investmentPagesMapping: { [key: string]: string } = {};
	@InjectConfig(ModelsExperienceConfigKey.SHOW_INVESTMENT_PDF_PAGES) private readonly showInvestmentPDFPages: boolean;
	@InjectConfig(ModelsExperienceConfigKey.PERFORMANCE_BOX_DISCLOSURE_ENABLED_FOR_BLK_GROUP_FAMILY)
	private readonly performanceBoxDisclosureEnabledBlkFamilies: Array<string> = [];
	@InjectQueryParam(MeQueryParamKey.GROUP_ID) private readonly groupId: string;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_COMMENTARY_KEYS_MAPPING) private readonly enableCommentaryKeysMapping: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_MODEL_OVERVIEW_PDF_PAGE) private readonly showModelOverviewPage: boolean;

	@InjectConfig(ModelsExperienceConfigKey.ENABLE_HOLDINGS_TABLE_HEADER_V2) private readonly enableHoldingsTableHeaderV2: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_ESG_PDF_PAGES) private readonly showEsgPage: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_PDF_GLOSSARY_PAGE) private readonly showGlossaryPage: boolean = true;
	@InjectConfig(PdfSharedConfigKey.SHOW_COBRANDING_DISCLOSURE) private readonly showCoBrandingDisclosure: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.EQUITIES_ASSET_CLASSES) private readonly equitiesAssetClasses: string[] =
		['Australian Equities', 'Developed Market Equities', 'Emerging Market Equities', 'Property & Infrastructure'];
	@InjectConfig(ModelsExperienceConfigKey.FIXED_INCOME_ASSET_CLASSES) private readonly fixedIncomeAssetClasses: string[] =
		['Australian Fixed Income', 'International Fixed Income', 'Global Fixed Income', 'Alternatives', 'Cash & Cash Alternatives', 'Cash'];
	@InjectConfig(ModelsExperienceConfigKey.PDF_RISK_PROFILE_MODULARITY_FAMILIES) private readonly pdfRiskProfileModularityFamiliesList = [];
	@InjectConfig(ModelsExperienceConfigKey.DISABLE_COVER_PAGE_IMAGE) private readonly disableCoverPageImage = false;
	@InjectConfig(ModelsExperienceConfigKey.PERFORMANCE_COMMENTARY_ENABLED_FOR_CMS) private readonly isPerfCommentaryEnabledForCms = false;

	public modelsName: string[];
	public holdingsAsOfDate: string;
	public familyName: string;
	public familyId: string;
	public aladdinTicker: string;
	public modelBenchmarkDisclaimer: string | undefined;
	public familySubtypeDcrPrefix: string;
	public inceptionDateText: string;
	public familyIdsDcrKey: string;
	public familyRoCodeKeys: string[] = [];
	public modelSubscriptLabel: string;
	public isBlkModel: boolean;
	public marketViewConfig: any;
	public modelsType: string;
	public investmentModelsType: string;
	public familyOverview: string;
	public tradeFrequency: string;
	public investmentVehicle: string;

	public footerDcr: string;
	public coverPageDcr: string;
	public cobrandingDisclosureDcr: string;
	public isFamilyPerformanceDisclaimerBoxVisible: boolean = false;
	public isEsgFamily: boolean = false;
	public latestAllocationData: string[] = [];
	public enableCoBrandingDisclosure: boolean = false;

	private readonly modelListFromAppendingModelName: string[] = ['model_portfolio_for_women_seed', 'MPUF_LPW'];

	constructor(
		private contentService: ContentService,
		private storeService: StoreService,
		private urlService: UrlService,
		private appStateStoreProxyService: ApplicationStateStoreProxyService,
		private modelsBrowseDataService: ModelsBrowseDataService,
		private marketViewConfigService: MarketViewConfigService,
		private localeUtilResolverService: LocaleUtilResolverService
	) {
		super();
	}

	public getReportCover(reportType: string, clientOutput: ClientOutput): string {
		const reportCover = this.getReportCoverPage(reportType, clientOutput.outputFlags);
		const contentReplacer = new Replacer({});
		clientOutput['date'] = dayjs().format('MMMM D, YYYY');
		if (!clientOutput['advisorLogo']) {
			clientOutput['advisorLogo'] = '';
		}
		const substitutionMap: Object = new Object();
		contentReplacer.populateDCRTags(reportCover, substitutionMap, this.contentService);
		return contentReplacer.resolveDataValues(reportCover, { ...clientOutput, ...substitutionMap }, contentReplacer.TAG_REGEX, '$');
	}

	public getClientConfig(outputFlags: OutputFlag[], customizationMap?: Map<string, { order: number; isVisible: boolean }>): AchoReportConfiguration {
		return null;
	}

	public getProposalConfig(outputFlags: OutputFlag[], customizationMap?: Map<string, { order: number; isVisible: boolean }>): AchoReportConfiguration {
		return null;
	}

	public getPortfolioSolutionsConfig(outputFlags: OutputFlag[], customizationMap?: Map<string, { order: number; isVisible: boolean }>): AchoReportConfiguration {
		return null;
	}

	public getLoadAdjDisclaimerDCRKey(): string {
		return this.contentService.get('PDF_LOAD_ADJUSTED_DISCLAIMER_2_' + this.aladdinTicker, '')
			? 'PDF_LOAD_ADJUSTED_DISCLAIMER_2_' + this.aladdinTicker
			: 'PDF_LOAD_ADJUSTED_DISCLAIMER_2';
	}

	public getSelfServiceConfig(
		outputFlags: OutputFlag[],
		customizationMap?: Map<string, { order: number; isVisible: boolean }>,
		selectedIds?: string[],
		familyIdentifier?: string,
		groupIdentifier?: string,
		excludePerformanceFromDownloadAll?: boolean
	): AchoReportConfiguration {
		this.getSelectedData(selectedIds, familyIdentifier, groupIdentifier);
		// if there is a cobranding logo, then enable the cobranding disclosure
		// applicable for AU use case only
		if (this.showCoBrandingDisclosure) {
			outputFlags.forEach(flag => {
				if (flag.getName() === 'hasValidAdvisorLogo') {
					this.enableCoBrandingDisclosure = flag.getValue();
				}
			});
		}

		//Locale based report config generation
		return new AchoReportConfiguration(
			this.getLocaleBasedReportConfig(outputFlags, customizationMap, excludePerformanceFromDownloadAll).filter(item => !!item),
			ViewCategory.FA_SELF_SERVICE_PDF,
			ADVISOR_LANDSCAPE_LAYOUT(this.familyName, this.footerDcr, this.modelSubscriptLabel),
			this.getDataComponentIdMapping(familyIdentifier, groupIdentifier)
		);
	}

	public getLocaleBasedReportConfig(outputFlags: OutputFlag[], customizationMap?: Map<string, { order: number; isVisible: boolean }>, excludePerformanceFromDownloadAll: boolean = false): ReportSection[] {
		// default to true when disable the modularity (CMS, Third party)
		const isPerformanceVisible = customizationMap?.get('FAMILY_PERFORMANCE_KEY')?.isVisible ?? true;
		const includePerformance = isPerformanceVisible && !excludePerformanceFromDownloadAll;
		const includePerformanceDcrSuffix = includePerformance ? '' : '_EXCLUDE_PERFORMANCE';
		const includePerformanceDcrSuffixForTradeNotice = includePerformance && this.isBlkModel ? '' : '_TRADE_NOTICE';
		const routeLocale = this.urlService.getParam('locale');
		const locale = routeLocale || this.localeUtilResolverService.getResolvedLocaleValue();
		if (locale === Countries.AU) {
			return this.getReportConfigAU(outputFlags, customizationMap, includePerformance, includePerformanceDcrSuffix, includePerformanceDcrSuffixForTradeNotice);
		} else if (locale === Countries.US) {
			return this.getReportConfigUS(outputFlags, customizationMap, includePerformance, includePerformanceDcrSuffix, includePerformanceDcrSuffixForTradeNotice);
		}
	}

	public getReportConfigUS(
		outputFlags: OutputFlag[],
		customizationMap?: Map<string, { order: number; isVisible: boolean }>,
		includePerformance?: boolean,
		includePerformanceDcrSuffix?: string,
		includePerformanceDcrSuffixForTradeNotice?: string
	) {
		const mode = ReportType.ModelsBrowse;
		return [
			// Cover Page
			new AdvisorReportCoverPage(this.familyName, this.coverPageDcr, this.modelSubscriptLabel, this.modelsType, this.cobrandingDisclosureDcr, this.enableCoBrandingDisclosure,
				this.disableCoverPageImage),
			// Model Overview Page
			new ReportSection(
				[new ModelOverview('Model Overview', this.aladdinTicker, this.familyOverview, this.tradeFrequency, this.investmentVehicle, this.modelBenchmarkDisclaimer)],
				'MODEL_OVERVIEW_SECTION_KEY',
				customizationMap.get('MODEL_OVERVIEW_SECTION_KEY')?.order,
				this.showModelOverviewPage && customizationMap.get('MODEL_OVERVIEW_SECTION_KEY')?.isVisible
			),
			// Investment guiding principles
			new ReportSection(
				[new InvestmentGuidingPrinciples('Investment Guiding Principles', this.investmentModelsType)],
				'INVESTMENT_GUIDING_PRINCIPLES_SECTION_KEY',
				customizationMap.get('INVESTMENT_GUIDING_PRINCIPLES_SECTION_KEY')?.order,
				this.showInvestmentPDFPages && !!this.investmentModelsType && customizationMap.get('INVESTMENT_GUIDING_PRINCIPLES_SECTION_KEY')?.isVisible
			),
			new ReportSection(
				[new InvestmentProcessComponent('Investment Process', this.investmentModelsType, this.holdingsAsOfDate)],
				'INVESTMENT_PROCESS_SECTION_KEY',
				customizationMap.get('INVESTMENT_PROCESS_SECTION_KEY')?.order,
				this.showInvestmentPDFPages && !!this.investmentModelsType && customizationMap.get('INVESTMENT_PROCESS_SECTION_KEY')?.isVisible
			),
			// Key Takeaways page
			new ReportSection(
				[new CommentaryComponent('Commentary', 'me.family.commentary', true, this.holdingsAsOfDate, true)],
				'KEY_TAKEAWAYS_SECTION_KEY',
				customizationMap.get('KEY_TAKEAWAYS_SECTION_KEY')?.order,
				this.enableCommentaryKeysMapping && customizationMap.get('KEY_TAKEAWAYS_SECTION_KEY')?.isVisible
			),
			// Trade rationale
			new ReportSection(
				[
					new CommentaryComponent('Commentary', 'me.family.commentary', true, this.holdingsAsOfDate),
					new PageBreakBusinessComponent('Page Break', '', '', TabUtil.isDataAvailable, !!this.marketViewConfig),
					new FreeFormComponent('PDF_MARKET_VIEW_H2', 'Asset Class Views', undefined, HeadingLevel.H2, !!this.marketViewConfig, ['text-14', 'text-bold']),
					new MarketViewsComponent('MARKET_VIEW_SECTION_KEY', '', this.marketViewConfig, false, this.holdingsAsOfDate, true, !!this.marketViewConfig),
					new FreeFormComponent('PDF_MARKET_VIEW_DISCLOSURE', 'PDF_MARKET_VIEW_DISCLOSURE', undefined, HeadingLevel.H1, !!this.marketViewConfig)
				],
				'TRADE_RATIONAL_SECTION_KEY',
				customizationMap.get('TRADE_RATIONAL_SECTION_KEY')?.order,
				customizationMap.get('TRADE_RATIONAL_SECTION_KEY')?.isVisible
			),
			// Performance Commentary
			new ReportSection(
				[new PerformanceCommentaryComponent('Performance Commentary', 'me.family.performance.commentary', includePerformance, true)],
				'PERFORMANCE_COMMENTARY_SECTION_KEY',
				customizationMap.get('PERFORMANCE_COMMENTARY_SECTION_KEY')?.order,
				(this.isPerfCommentaryEnabledForCms || this.isBlkModel) && customizationMap.get('PERFORMANCE_COMMENTARY_SECTION_KEY')?.isVisible
			),
			// Latest Holdings
			new ReportSection(
				[
					new FamilyLatestHoldingsHeaderComponent(
						'Latest Holding',
						'me.family.latest.holdings.header.pdf',
						true,
						this.modelsName,
						this.holdingsAsOfDate,
						this.latestAllocationData,
						this.familyId
					),
					new FamilyLatestHoldingsBodyComponent('Latest Holding Table', 'me.family.latest.holdings.body.pdf', true, this.modelsName, includePerformanceDcrSuffix)
				],
				'LATEST_HOLDINGS_SECTION_KEY',
				customizationMap.get('LATEST_HOLDINGS_SECTION_KEY')?.order,
				customizationMap.get('LATEST_HOLDINGS_SECTION_KEY')?.isVisible
			),
			// Changes to Holdings
			new ReportSection(
				[new FamilyChangesToHoldingsComponent('PDF_CHANGES_TO_HOLDINGS', 'me.family.changesToHoldings.pdf', true, this.modelsName, this.holdingsAsOfDate)],
				'CHANGES_TO_HOLDINGS_SECTION_KEY',
				customizationMap.get('CHANGES_TO_HOLDINGS_SECTION_KEY')?.order,
				customizationMap.get('CHANGES_TO_HOLDINGS_SECTION_KEY')?.isVisible
			),
			// Performance
			new ReportSection(
				[
					new FamilyPerformanceComponent(
						'PDF_FAMILY_PERFORMANCE',
						'me.family.performance',
						true,
						this.aladdinTicker,
						this.inceptionDateText,
						includePerformance,
						this.familySubtypeDcrPrefix,
						this.isFamilyPerformanceDisclaimerBoxVisible,
						this.modelBenchmarkDisclaimer
					)
				],
				'FAMILY_PERFORMANCE_KEY',
				customizationMap.get('FAMILY_PERFORMANCE_KEY')?.order,
				customizationMap.get('FAMILY_PERFORMANCE_KEY')?.isVisible
			),
			// Fund Perf and Fees
			new ReportSection(
				[
					new SectionTitleComponent('APPENDIX_H1', 'APPENDIX', new BusinessComponentOptions('me.performance.fund.pdf'), true, null, false, true),
					new StandardizedPerformanceNavComponent('Quarterly Returns NAV', 'me.performance.fund.pdf', mode, true, true, includePerformance),
					new StandardizedPerformanceMarketComponent('Quarterly Returns Market', 'me.performance.fund.pdf', mode, true, includePerformance),
					new LoadAdjustedReturnsComponent('Load Adjusted Returns', 'me.performance.fund.pdf', mode, this.getLoadAdjDisclaimerDCRKey(), true, includePerformance),
					new FeesComponent('Performance Fees', 'me.performance.fees.pdf', mode, true, true),
					new SectionTitleComponent('PDF_GLOSSARY_H1', 'GLOSSARY', new BusinessComponentOptions('', this.showGlossaryPage), false, null, false, true, HeadingLevel.H2),
					new GlossaryComponent(`MB_ADVISOR_GLOSSARY${includePerformanceDcrSuffix}`, 1, `MB_ADVISOR_GLOSSARY${includePerformanceDcrSuffix}`, this.showGlossaryPage)
				],
				'FUND_PERFORMANCE_SECTION_KEY',
				customizationMap.get('FUND_PERFORMANCE_SECTION_KEY')?.order,
				customizationMap.get('FUND_PERFORMANCE_SECTION_KEY')?.isVisible
			),
			// Disclosure
			this.getDisclosureSection(customizationMap, includePerformanceDcrSuffix, includePerformanceDcrSuffixForTradeNotice)
		];
	}

	public getReportConfigAU(
		outputFlags: OutputFlag[],
		customizationMap?: Map<string, { order: number; isVisible: boolean }>,
		includePerformance?: boolean,
		includePerformanceDcrSuffix?: string,
		includePerformanceDcrSuffixForTradeNotice?: string
	) {
		return [
			// Cover Page
			new AdvisorReportCoverPage(this.familyName, this.coverPageDcr, this.modelSubscriptLabel, this.modelsType, this.cobrandingDisclosureDcr, this.enableCoBrandingDisclosure,
				this.disableCoverPageImage),
			// Performance, Performance Commentary (Subsection)
			new ReportSection(
				[
					new FamilyPerformanceComponent(
						'PDF_FAMILY_PERFORMANCE',
						'me.family.performance',
						true,
						this.aladdinTicker,
						this.inceptionDateText,
						includePerformance,
						this.familySubtypeDcrPrefix,
						this.isFamilyPerformanceDisclaimerBoxVisible
					),
					new PageBreakBusinessComponent('Page Break', 'me.family.performance.commentary', 'family.performance.commentary', TabUtil.isDataAvailable),
					new PerformanceCommentaryComponent(
						'Performance Commentary',
						'me.family.performance.commentary',
						includePerformance,
						(this.isPerfCommentaryEnabledForCms || this.isBlkModel) && customizationMap.get('PERFORMANCE_COMMENTARY_SECTION_KEY')?.isVisible
					)

				],
				'FAMILY_PERFORMANCE_KEY',
				customizationMap.get('FAMILY_PERFORMANCE_KEY')?.order,
				customizationMap.get('FAMILY_PERFORMANCE_KEY')?.isVisible
			),
			// Latest Holdings
			new ReportSection(
				[
					new FamilyLatestHoldingsHeaderComponent(
						'Latest Holding',
						'me.family.latest.holdings.header.pdf',
						true,
						this.modelsName,
						this.holdingsAsOfDate,
						this.latestAllocationData,
						this.familyId
					),
					new FamilyLatestHoldingsBodyComponent('Latest Holding Table', 'me.family.latest.holdings.body.pdf', true, this.modelsName, includePerformanceDcrSuffix)
				],
				'LATEST_HOLDINGS_SECTION_KEY',
				customizationMap.get('LATEST_HOLDINGS_SECTION_KEY')?.order,
				customizationMap.get('LATEST_HOLDINGS_SECTION_KEY')?.isVisible
			),
			// Trade Rationale, Changes to Holdings (Subsection)
			new ReportSection(
				[
					new CommentaryComponent('Commentary', 'me.family.commentary', true, this.holdingsAsOfDate),
					new PageBreakBusinessComponent('Page Break', '', '', TabUtil.isDataAvailable, !!this.marketViewConfig),
					new FreeFormComponent('PDF_MARKET_VIEW_H2', 'Asset Class Views', undefined, HeadingLevel.H2, !!this.marketViewConfig, ['text-14', 'text-bold']),
					new MarketViewsComponent('MARKET_VIEW_SECTION_KEY', '', this.marketViewConfig, false, this.holdingsAsOfDate, true, !!this.marketViewConfig),
					new FreeFormComponent('PDF_MARKET_VIEW_DISCLOSURE', 'PDF_MARKET_VIEW_DISCLOSURE', undefined, HeadingLevel.H1, !!this.marketViewConfig),
					new PageBreakBusinessComponent('Page Break', 'me.family.changesToHoldings.pdf', 'changesToHoldings', TabUtil.isDataAvailable),
					new FamilyChangesToHoldingsComponent(
						'PDF_CHANGES_TO_HOLDINGS',
						'me.family.changesToHoldings.pdf',
						customizationMap.get('CHANGES_TO_HOLDINGS_SECTION_KEY')?.isVisible,
						this.modelsName,
						this.holdingsAsOfDate
					)
				],
				'TRADE_RATIONAL_SECTION_KEY',
				customizationMap.get('TRADE_RATIONAL_SECTION_KEY')?.order,
				customizationMap.get('TRADE_RATIONAL_SECTION_KEY')?.isVisible
			),
			// ESG Uplift
			new ReportSection(
				[new EsgUpliftComponent('PDF_ESG_UPLIFT', 'me.esg.uplift')],
				'FAMILY_ESG_UPLIFT_KEY',
				customizationMap.get('FAMILY_ESG_UPLIFT_KEY')?.order,
				this.showEsgPage && this.isEsgFamily //Visibility is derived on these flags
			),
			// ESG Carbon Emissions
			new ReportSection(
				[
					new EsgMsciRatingsComponent('PDF_ESG_RATINGS'),
					new EsgLowerCarbonFootprintComponent('PDF_LOWER_CARBON_FOOTPRINT'),
					new ESGScoreImpact(
						'ESG Carbon Score Impact',
						'me.esg.carbon.intensity',
						this.contentService,
						{
							headingDcr: 'CARBON_EMISSIONS_EQUIVALENTS_HEADER_PDF',
							heading: 'CARBON EMISSIONS EQUIVALENTS',
							headlineDcr: 'CARBON_EMISSIONS_EQUIVALENTS_SUBTEXT_PDF',
							headline: `CARBON_EMISSIONS_EQUIVALENTS_SUBTEXT_PDF`,
							showDisclaimer: true
						} as ESGMetricsHeadings,
						false,
						false,
						['text-10']
					)
				],
				'FAMILY_ESG_RATINGS_KEY',
				customizationMap.get('FAMILY_ESG_RATINGS_KEY')?.order,
				this.showEsgPage && this.isEsgFamily //Visibility is derived on these flags
			),
			// Disclosure
			this.getDisclosureSection(customizationMap, includePerformanceDcrSuffix, includePerformanceDcrSuffixForTradeNotice)
		];
	}

	public getDataComponentIdMapping(familyIdentifier?: string, groupIdentifier?: string): { [id: string]: Observable<string[]> } {
		const modelIds: Observable<string[]> = this.storeService.selectAll(ModelsBrowseStoreConfig.families).pipe(
			first(families => !families.isLoading && !isEmpty(get(families.entities, this.familyId || familyIdentifier))),
			map(families => {
				return get(families.entities, [this.familyId || familyIdentifier, 'models'], [])
					.filter(portfolio => portfolio.groups.map(element => element.id).includes(this.groupId || groupIdentifier))
					.map(model => model.id);
			})
		);
		const familyIds: Observable<string[]> = of([this.familyId || familyIdentifier]);

		return {
			'me.family.commentary': familyIds,
			'me.family.performance.commentary': familyIds,
			'me.family.latest.holdings.header.pdf': familyIds,
			'me.family.latest.holdings.body.pdf': modelIds,
			'me.family.changesToHoldings.pdf': modelIds,
			'me.family.performance': familyIds,
			'me.performance.fund.pdf': familyIds,
			'me.performance.fees.pdf': familyIds,
			'me.esg.uplift': familyIds,
			'me.esg.carbon.intensity': familyIds
		};
	}

	public getSelectedData(selectedIds?: string[], familyIdentifier?: string, groupIdentifier?: string): void {
		this.familyId = this.urlService.getModelFamilyId() || familyIdentifier;
		this.modelsBrowseDataService
			.getFamilyNameId(this.familyId)
			.pipe(
				switchMap(familyNameId => {
					return combineLatest([
						this.storeService.selectAll(ModelsBrowseStoreConfig.families),
						this.storeService.selectById(ModelsBrowseStoreConfig.familyPerformance, this.familyId),
						this.storeService.selectAll(ModelsBrowseStoreConfig.familyHoldings),
						this.appStateStoreProxyService.getApplicationState(),
						this.marketViewConfigService.fetchConfig(familyNameId),
						this.modelsBrowseDataService.getAladdinTicker(this.familyId),
						this.modelsBrowseDataService.getBenchmarkDisclaimer(this.familyId)
					]);
				}),
				first()
			)
			.subscribe(([families, familyPerformance, holdings, appState, marketViewConfig, aladdinTicker, modelBenchmarkDisclaimer]) => {
				this.aladdinTicker = aladdinTicker;
				this.modelBenchmarkDisclaimer = modelBenchmarkDisclaimer;
				const familyId = this.aladdinTicker;
				this.familyIdsDcrKey = new DcrKeyPipe().transform(familyId);
				this.modelsName = get(families.entities, [this.familyId, 'models'], [])
					.filter(portfolio => portfolio.groups.map(element => element.id).includes(this.groupId || groupIdentifier))
					.filter(portfolio => this.pdfRiskProfileModularityFamiliesList.includes(this.familyId) && selectedIds?.length ? selectedIds.includes(portfolio.id) : true)
					.map(model => {
						return ModelNameUtil.getModelName(model, null, 'shortName');
					});
				const firstModelIdInSelectedFamily = get(families.entities, `${this.familyId}.models[0].id`, '');
				const defaultAsOfDate = this.contentService.get(`${this.aladdinTicker}_HOLDING_AS_OF_DATE_MISSING`, `${this.aladdinTicker}_HOLDING_AS_OF_DATE_MISSING`);

				this.getDcrKeysForModels(families);

				const familyHoldingsData: Array<ModelPortfolio> = FamiliesUtil.getOrderedFamilyHoldingData(
					families.entities,
					this.familyId,
					this.groupId || groupIdentifier,
					holdings,
					selectedIds
				);
				if (this.enableHoldingsTableHeaderV2) {
					this.latestAllocationData = this.getLatestAllocationRow(familyHoldingsData);
				}
				this.inceptionDateText = this.modelsBrowseDataService.getInceptionDateText(
					families.entities,
					familyHoldingsData,
					this.familyId,
					this.pdfRiskProfileModularityFamiliesList.includes(this.familyId) && selectedIds?.length ? selectedIds : []
				);
				this.holdingsAsOfDate = get(holdings.entities, `${firstModelIdInSelectedFamily}.metadata.currentRebalanceDate`, defaultAsOfDate);
				this.modelSubscriptLabel = get(families.entities, `${this.familyId}.modelSubscriptLabel`, '');
				this.isEsgFamily = get(families.entities, `${this.familyId}.preferences`, '') === 'esg';
				this.familyName = get(appState, 'generateReportDataState.additionalHeaderInfo');
				const isExemptFromAppendingModelName =
					this.modelListFromAppendingModelName.includes(this.familyId) || this.modelListFromAppendingModelName.includes(this.aladdinTicker);

				if (!this.familyName.toLowerCase().includes('models') && !isExemptFromAppendingModelName) {
					this.familyName += StringUtil.delimitedAndPluralizeString(' ${Model}', this.modelsName.length > 1);
				}
				this.marketViewConfig = !isEmpty(marketViewConfig) ? marketViewConfig : null;

				const modelCategoryType = families?.entities?.[this.familyId]?.modelCategoryType;
				this.modelsType = this.modelCategoryMapping?.[modelCategoryType] || '';

				this.investmentModelsType = this.investmentPagesMapping?.[modelCategoryType];

				this.isFamilyPerformanceDisclaimerBoxVisible =
					(this.groupId || groupIdentifier) !== SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP ||
					this.performanceBoxDisclosureEnabledBlkFamilies.includes(this.aladdinTicker);
				this.familyOverview = families.entities[this.familyId]?.marketingDescription;
				this.tradeFrequency = families.entities[this.familyId]?.tradeFrequency;
				this.investmentVehicle = families.entities[this.familyId]?.vehicle;
			});
	}

	private getDcrKeysForModels(families: any): void {
		// subtype
		const familySubtype: string = families.entities?.[this.familyId]?.subType ?? '';
		this.isBlkModel = familySubtype === 'BLK';
		this.familySubtypeDcrPrefix = this.isBlkModel ? '' : familySubtype.toUpperCase() + '_'; // no extra subtype needed for blk and use default key

		//RO code
		const responseRoCode = families.entities?.[this.familyId]?.roCode ?? [];
		this.familyRoCodeKeys = isEmpty(responseRoCode) ? [`${this.familyIdsDcrKey}_RO`] : responseRoCode;

		//pdf key
		const footerDcr = this.contentService.get(`DCR_MB_${this.aladdinTicker}_LANDSCAPE_FOOTER_TEXT`, null)
			? `DCR_MB_${this.aladdinTicker}_LANDSCAPE_FOOTER_TEXT`
			: `DCR_MB_${this.familySubtypeDcrPrefix}LANDSCAPE_FOOTER_TEXT`;
		const coverPageDcr = this.contentService.get(`DCR_MB_${this.aladdinTicker}_COVER_PAGE_DISCLOSURE`, null)
			? `DCR_MB_${this.aladdinTicker}_COVER_PAGE_DISCLOSURE`
			: `DCR_MB_${this.familySubtypeDcrPrefix}COVER_PAGE_DISCLOSURE`;
		// TODO dpurohit: confirm if we can have family specific cobranding disclosure
		const cobrandingDisclosureDcr = 'DCR_MB_COBRANDING_PDF_DISCLOSURE';
		this.footerDcr = new DcrKeyPipe().transform(footerDcr);
		this.coverPageDcr = new DcrKeyPipe().transform(coverPageDcr);
		this.cobrandingDisclosureDcr = new DcrKeyPipe().transform(cobrandingDisclosureDcr);
	}

	private getLatestAllocationRow(familyHoldingsData: Array<ModelPortfolio>): string[] {
		const latestAllocationVal = familyHoldingsData.map((holdingsRow, index) => {
			const latestAllocationTopCell: number[] = [];
			const latestAllocationBottomCell: number[] = [];
			const securities = holdingsRow?.components;
			securities?.forEach(security => {
				const category = security?.posInfo?.acAssetClass?.stringValue;
				const formattedAcAssetClass = new MappedValueFormatter(ASSET_CLASS_LABEL_MAPPING).format(category);
				const weight = security?.weight * 100;
				if (this.equitiesAssetClasses.includes(formattedAcAssetClass)) {
					latestAllocationTopCell[index] = (latestAllocationTopCell?.[index] || 0) + +weight;
				}
				if (this.fixedIncomeAssetClasses.includes(formattedAcAssetClass)) {
					latestAllocationBottomCell[index] = (latestAllocationBottomCell?.[index] || 0) + +weight;
				}
			});
			const bottomCell = (latestAllocationBottomCell?.[index] || 0).toFixed(1);
			const latestAllocation = parseFloat((latestAllocationTopCell?.[index] || 0).toFixed(1)) + '/' + parseFloat(bottomCell);
			return latestAllocation;
		});
		return latestAllocationVal;
	}

	private getDisclosureSection(customizationMap, includePerformanceDcrSuffix, includePerformanceDcrSuffixForTradeNotice): ReportSection {
		return new ReportSection(
			[
				new SectionTitleComponent('PDF_DISCLOSURE_H1', 'IMPORTANT INFORMATION', new BusinessComponentOptions(''), false, null, false, true),
				new FreeFormComponent(
					`MB_${this.familySubtypeDcrPrefix}CLOSING_DISCLOSURE_ADVISOR${includePerformanceDcrSuffix}`,
					`MB_${this.familySubtypeDcrPrefix}CLOSING_DISCLOSURE_ADVISOR${includePerformanceDcrSuffix}`
				),
				...this.familyRoCodeKeys.map(
					roCodeKey => new FreeFormComponent(`${roCodeKey}${includePerformanceDcrSuffixForTradeNotice}`, `${roCodeKey}${includePerformanceDcrSuffixForTradeNotice}`)
				)
			],
			'DISCLOSURE_SECTION_KEY',
			customizationMap.get('DISCLOSURE_SECTION_KEY')?.order,
			customizationMap.get('DISCLOSURE_SECTION_KEY')?.isVisible
		);
	}
}
